import * as React from 'react';

import { ProductChangesList } from '../change-lists/product-change-list';
import { EntityUpdateRequest } from '../taskAuthorizations';
import { EntityUpdateRequestCard, MILESTONE, Signatory } from '../entity-update-request/entity-update-request-card';
import { PostAction, RejectAction, SignAction } from '../entity-update-request/entity-update-request-actions';
import { ProductManagementVendorModalComponent } from './product-management-vendor-modal.component';
import { ProductManagementMoreProductsSectionComponent } from './product-management-more-products-section.component';
import Manager from '../../managers/manager';
import { hasFeatureAccess } from '../../login/auth';
import { ManagerPermission } from '../../permission-profiles/permission';
import { displayTime } from '../../utils/utils';
import { ProductManagementUploadUpdateFileComponent } from './product-management-upload-update-file.component';
import { ProductManagementReworkUpdateRequestComponent } from './product-management-rework-update-request.component';

interface Props {
    updateRequest: EntityUpdateRequest;
    manager: Manager;
    isShowingPendingUpdates: boolean;
}

export function ProductManagementAuthorizationCardComponent({
    updateRequest,
    manager,
    isShowingPendingUpdates,
}: Props) {
    const signatures = getSignatures(updateRequest);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const signatureChain = updateRequest?.signatureChain as any;
    const isRejected = Boolean(updateRequest?.deletedAt || signatureChain?.rejectedAt);
    const isVerified = Boolean(updateRequest?.authorizedAt && updateRequest?.authorizedByManager);
    const isSettled = signatureChain?.isSettled ?? true;
    const signed =
        signatureChain?.signatures
            ?.filter((signature) => signature?.signedAt)
            .sort((a, b) => a.signatureLevel - b.signatureLevel) ?? [];
    const isSignedByUser = signed.find((signature) => signature.signedByManagerId === Number(manager?.id));
    const canSign =
        hasFeatureAccess({ manager }, ManagerPermission.productGroupUpdatePricesPostLevelOne) ||
        hasFeatureAccess({ manager }, ManagerPermission.productGroupUpdatePricesPostLevelTwo);
    const isParkedByUser = Number(updateRequest.createdByManager.id) === Number(manager?.id);
    const canVerify =
        hasFeatureAccess({ manager }, ManagerPermission.productGroupUpdatePricesVerify) && !isParkedByUser;

    const showSignAction = !isSettled && !isSignedByUser && canSign;
    const showPostAction = !isVerified && canVerify && !isParkedByUser;
    const showActions = !((isVerified || isRejected) && isSettled);
    const canReject = showPostAction || showSignAction;
    const showPostDisclaimer = isParkedByUser && !isVerified && !isRejected;
    const comment = updateRequest.comment || signatureChain?.comment;
    const documentUploadedByManager = updateRequest?.documentUploadedByManager?.name;

    return (
        <EntityUpdateRequestCard>
            <EntityUpdateRequestCard.EntityDetails>
                <p>
                    <strong>Product Group:</strong> {updateRequest?.title}
                </p>
                {updateRequest.validFrom && (
                    <p>
                        <strong>Valid from: </strong>
                        {displayTime(updateRequest.validFrom)}
                    </p>
                )}
                {comment && (
                    <p>
                        <strong>Comment: </strong>
                        {comment}
                    </p>
                )}
                {isShowingPendingUpdates ? null : (
                    <ProductManagementVendorModalComponent groupId={updateRequest.entityId} />
                )}
                {isRejected ? (
                    <ProductManagementReworkUpdateRequestComponent
                        productGroup={String(updateRequest?.title)}
                        productGroupId={updateRequest.entityId}
                        updates={updateRequest.updates}
                        validFrom={updateRequest.validFrom}
                    />
                ) : (
                    <ProductManagementUploadUpdateFileComponent
                        asyncTaskId={updateRequest?.updateFile?.id}
                        entityUpdateRequestId={updateRequest.id}
                        productGroupId={updateRequest.entityId}
                    />
                )}
            </EntityUpdateRequestCard.EntityDetails>
            <EntityUpdateRequestCard.Signatories>
                {documentUploadedByManager && (
                    <Signatory
                        author={documentUploadedByManager}
                        date={String(updateRequest?.updateFile?.created_at)}
                        type={MILESTONE.UPLOAD}
                    />
                )}
                {signatures.map(([type, { date, author }]) => (
                    <RenderSignatory author={author} date={date} key={type} type={type} />
                ))}
                {signed.map(
                    (signature: {
                        id?: number;
                        signedByManager: { name: string };
                        signedAt: string;
                        signatureLevel: number;
                    }) => (
                        <Signatory
                            author={signature.signedByManager.name}
                            date={signature.signedAt}
                            key={signature?.id}
                            level={signature.signatureLevel}
                            type={MILESTONE.SIGNED}
                        />
                    )
                )}
                {signatureChain?.rejectedAt && (
                    <Signatory
                        author={signatureChain?.rejectedByManager?.name}
                        date={signatureChain?.rejectedAt}
                        type={MILESTONE.REJECTED}
                    />
                )}
            </EntityUpdateRequestCard.Signatories>
            <EntityUpdateRequestCard.Details>
                <ProductChangesList updateRequest={updateRequest} updates={updateRequest.updates}>
                    <ProductManagementMoreProductsSectionComponent
                        excludedProducts={updateRequest.updates.map((update) => update.path)}
                        groupId={updateRequest.entityId}
                        isShowingPendingUpdates={isShowingPendingUpdates}
                        validFrom={new Date(updateRequest.createdAt)}
                    />
                </ProductChangesList>
                {isShowingPendingUpdates && (
                    <ProductManagementVendorModalComponent groupId={updateRequest.entityId} inline />
                )}
            </EntityUpdateRequestCard.Details>
            <EntityUpdateRequestCard.Actions>
                {showActions && !showPostDisclaimer && (
                    <>
                        {canReject && <RejectAction updateRequest={updateRequest} />}
                        {showPostAction && <PostAction updateRequest={updateRequest}>Verify</PostAction>}
                        {showSignAction && (
                            <SignAction updateRequest={updateRequest}>Sign Level {signed.length + 1}</SignAction>
                        )}
                    </>
                )}
                {showPostDisclaimer && (
                    <p>
                        <strong>You cannot post this request because you parked it</strong>
                    </p>
                )}
            </EntityUpdateRequestCard.Actions>
        </EntityUpdateRequestCard>
    );
}

const RenderSignatory = ({ type, ...props }: { type: string; date: string; author: string }) => {
    switch (type) {
        case 'parked':
            return <Signatory {...props} type={MILESTONE.PARKED} />;
        case 'verified':
            return <Signatory {...props} type={MILESTONE.VERIFIED} />;
        case 'rejected':
            return <Signatory {...props} type={MILESTONE.REJECTED} />;
        default:
            return null;
    }
};

function getSignatures(updateRequest: EntityUpdateRequest) {
    const signatures = {
        parked: {
            date: updateRequest?.createdAt,
            author: updateRequest?.createdByManager?.name,
        },
        verified: {
            date: updateRequest?.authorizedAt,
            author: updateRequest?.authorizedByManager?.name,
        },
        rejected: {
            date: updateRequest?.deletedAt,
            author: updateRequest?.deletedByManager?.name,
        },
    } as Record<'parked' | 'verified' | 'rejected', { date: string; author: string }>;

    return Object.entries(signatures).filter(([, { date, author }]) => date && author) as Array<
        ['parked' | 'verified' | 'rejected', { date: string; author: string }]
    >;
}
